import React, { useState, useCallback } from 'react';
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Button,
  Typography,
  Box,
  FormControlLabel,
  Checkbox,
  Alert,
  CircularProgress,
  IconButton,
} from '@mui/material';
import { AlertTriangle, X } from 'lucide-react';
import { API_BASE_URL } from '../utils/api';

/**
 * Confirm + execute destructive delete of a finished (succeeded/failed)
 * build. Wipes all R2 artifacts for that build unconditionally; the
 * user must tick an extra checkbox to also purge the MEGA copies.
 */
export default function BuildDeleteDialog({ open, build, onClose, onDeleted, getToken }) {
  const hasMegaUpload = !!build?.megaUploadedAt;
  const [alsoDeleteMega, setAlsoDeleteMega] = useState(hasMegaUpload);
  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState('');

  // Reset the checkbox default whenever the target build changes.
  React.useEffect(() => {
    setAlsoDeleteMega(hasMegaUpload);
    setError('');
  }, [build?.buildId, hasMegaUpload]);

  const handleDelete = useCallback(async () => {
    if (!build) return;
    setSubmitting(true);
    setError('');
    try {
      const token = await getToken();
      const url = `${API_BASE_URL}/api/builds/${encodeURIComponent(build.buildId)}${
        alsoDeleteMega && hasMegaUpload ? '?deleteFromMega=true' : ''
      }`;
      const res = await fetch(url, {
        method: 'DELETE',
        headers: { Authorization: `Bearer ${token}` },
      });
      if (!res.ok) {
        const body = await res.json().catch(() => ({}));
        throw new Error(body.message || `HTTP ${res.status}`);
      }
      onDeleted && onDeleted(build.buildId);
    } catch (e) {
      setError(e.message || 'Failed to delete build');
    } finally {
      setSubmitting(false);
    }
  }, [build, alsoDeleteMega, hasMegaUpload, getToken, onDeleted]);

  if (!build) return null;

  return (
    <Dialog
      open={open}
      onClose={submitting ? undefined : onClose}
      maxWidth="xs"
      fullWidth
      PaperProps={{
        sx: {
          bgcolor: 'background.paper',
          border: '1px solid',
          borderColor: 'divider',
          backgroundImage: 'none',
        },
      }}
    >
      <DialogTitle sx={{ pr: 6, display: 'flex', alignItems: 'center', gap: 1 }}>
        <AlertTriangle size={18} color="#f87171" />
        <Typography variant="h6" sx={{ fontSize: '1rem', fontWeight: 600 }}>
          Delete build
        </Typography>
      </DialogTitle>
      <IconButton
        aria-label="close"
        onClick={onClose}
        disabled={submitting}
        sx={{ position: 'absolute', right: 8, top: 8, color: 'text.secondary' }}
      >
        <X size={18} />
      </IconButton>
      <DialogContent sx={{ pt: 1, display: 'flex', flexDirection: 'column', gap: 1.5 }}>
        <Typography sx={{ fontSize: '0.85rem', color: 'text.primary' }}>
          {build.gameTitle || build.slug || '—'}{' '}
          <Box component="span" sx={{ color: '#a78bfa' }}>V{build.version}</Box>
        </Typography>
        <Typography sx={{ fontSize: '0.78rem', color: 'text.secondary' }}>
          All R2 artifacts for this build will be permanently removed
          {build.platforms?.includes('pwa') ? ', including the hosted PWA tree' : ''}.
          This cannot be undone.
        </Typography>

        {hasMegaUpload && (
          <FormControlLabel
            control={
              <Checkbox
                size="small"
                checked={alsoDeleteMega}
                onChange={(e) => setAlsoDeleteMega(e.target.checked)}
                disabled={submitting}
                sx={{
                  color: 'rgba(255,255,255,0.4)',
                  '&.Mui-checked': { color: '#f87171' },
                }}
              />
            }
            label={
              <Typography sx={{ fontSize: '0.78rem', color: 'text.secondary' }}>
                Also delete uploaded files from MEGA
              </Typography>
            }
            sx={{ mt: 0.5 }}
          />
        )}

        {error && <Alert severity="error" sx={{ fontSize: '0.8rem' }}>{error}</Alert>}
      </DialogContent>
      <DialogActions sx={{ px: 3, pb: 2 }}>
        <Button onClick={onClose} disabled={submitting} sx={{ textTransform: 'none' }}>
          Cancel
        </Button>
        <Button
          onClick={handleDelete}
          disabled={submitting}
          variant="contained"
          sx={{
            textTransform: 'none',
            bgcolor: '#f87171',
            '&:hover': { bgcolor: '#ef4444' },
          }}
          startIcon={submitting ? <CircularProgress size={14} color="inherit" /> : null}
        >
          {submitting ? 'Deleting…' : 'Delete'}
        </Button>
      </DialogActions>
    </Dialog>
  );
}
